/**
 * Shared PAM UI state components.
 *
 * Exports:
 *   PamLoading      — spinner with accessible aria-label
 *   PamError        — error alert with optional retry callback
 *   PamEmpty        — empty-state info alert
 *   PamConfirmDialog — generic confirm/cancel dialog for destructive ops
 *
 * Migrated off MUI (OIDCPAM Gate-6 Phase 3). All four are local themed
 * equivalents rather than direct @devnw/primitives consumers:
 *  - PamError, PamEmpty, PamConfirmDialog: the candidate primitives pieces
 *    (ErrorView, AlertBanner, Modal/ConfirmDialog) are all built on
 *    react-native primitives and `useSpyderTheme()`, which throws outside a
 *    <SpyderThemeProvider> — this app only provides a styled-components
 *    ThemeProvider (see .superpowers/sdd/oidcpam-gate6-shared.md). primitives'
 *    EmptyState IS styled-components-based and theme-safe, but its fixed
 *    300px-tall hero layout (icon + title + description) is a poor fit for
 *    PamEmpty's ~26 call sites, most of which render a single compact
 *    caption inside a table/card.
 *  - PamLoading: primitives' `Loading` subpath (LogoLoader) is
 *    styled-components-based and theme-safe, but its barrel
 *    (`Loading/index.ts`) also re-exports `Spinner`, which imports
 *    `WaSpinner` from `@devnw/webawesome` → that package's vendored,
 *    pre-built `vendor/dist/react/index.js` is raw untranspiled ESM with no
 *    `.ts`/`.tsx` extension, so it falls outside this app's jest `transform`
 *    map (only `.tsx?` is configured) even though `transformIgnorePatterns`
 *    allows `@devnw/*` through — `SyntaxError: Cannot use import statement
 *    outside a module` when any test imports that subpath. Confirmed this
 *    is a toolchain gap, not fixable by touching only the six migrated
 *    files, so a local zero-dependency spinner is used instead.
 *
 * All local styled-components below use `ut(asUnified(theme))` / optional
 * chaining with literal fallbacks, so this file never throws even without a
 * styled-components ThemeProvider.
 */
import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'

// ---------------------------------------------------------------------------
// PamLoading
// ---------------------------------------------------------------------------

interface PamLoadingProps {
  /** Accessible label for the spinner (default: "Loading…") */
  label?: string
}

const LoadingBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => ut(asUnified(theme)).space('lg')} 0;
`

/** Zero-dependency spinner — see the file header for why @devnw/primitives'
 *  Loading/Spinner subpaths aren't used here. `role="status"` + `aria-live`
 *  announce the loading state to assistive tech (an indeterminate spinner is
 *  not a `role="progressbar"`, which implies a known value). */
const SpinnerRing = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  border: 3px solid
    ${({ theme }) => `${asUnified(theme).colors?.primary ?? '#1a56db'}33`};
  border-top-color: ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
  animation: pam-spin 0.9s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @keyframes pam-spin {
    to {
      transform: rotate(360deg);
    }
  }
`

export const PamLoading = ({
  label = 'Loading…',
}: PamLoadingProps): JSX.Element => {
  return (
    <LoadingBox aria-busy="true">
      <SpinnerRing role="status" aria-live="polite" aria-label={label} $size={32} />
    </LoadingBox>
  )
}

// ---------------------------------------------------------------------------
// PamError
// ---------------------------------------------------------------------------

interface PamErrorProps {
  /** Human-readable error message */
  message: string
  /** Optional retry callback — renders a "Retry" button when provided */
  onRetry?: () => void
}

const ErrorBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  margin: ${({ theme }) => ut(asUnified(theme)).space('md')} 0;
  padding: ${({ theme }) => ut(asUnified(theme)).space('sm')};
  border: 1px solid ${({ theme }) => asUnified(theme).colors?.danger ?? '#991b1b'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: ${({ theme }) => `${asUnified(theme).colors?.danger ?? '#991b1b'}14`};
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
`

const RetryButton = styled.button`
  flex-shrink: 0;
  min-height: 36px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xxs')} ${ut(asUnified(theme)).space('sm')}`};
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#cbd5e1'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: none;
  color: ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
  font-weight: 600;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: 2px;
  }
`

export const PamError = ({ message, onRetry }: PamErrorProps): JSX.Element => {
  return (
    <ErrorBox role="alert">
      <span>{message}</span>
      {onRetry && (
        <RetryButton type="button" onClick={onRetry}>
          Retry
        </RetryButton>
      )}
    </ErrorBox>
  )
}

// ---------------------------------------------------------------------------
// PamEmpty
// ---------------------------------------------------------------------------

interface PamEmptyProps {
  /** Message shown when there are no items (default: "No items found.") */
  message?: string
}

const EmptyText = styled.p`
  margin: 0;
  padding: ${({ theme }) => ut(asUnified(theme)).space('xl')} 0;
  text-align: center;
  color: ${({ theme }) => asUnified(theme).text?.secondary ?? '#64748b'};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
`

export const PamEmpty = ({
  message = 'No items found.',
}: PamEmptyProps): JSX.Element => {
  return <EmptyText>{message}</EmptyText>
}

// ---------------------------------------------------------------------------
// PamConfirmDialog
// ---------------------------------------------------------------------------

interface PamConfirmDialogProps {
  /** Controls dialog visibility */
  open: boolean
  /** Dialog heading */
  title: string
  /** Dialog body text */
  body: string
  /** Label for the confirm button (default: "Confirm") */
  confirmLabel?: string
  /** Label for the cancel button (default: "Cancel") */
  cancelLabel?: string
  /** Called when the user clicks the confirm button */
  onConfirm: () => void
  /** Called when the user clicks cancel, the backdrop, or presses Escape */
  onCancel: () => void
  /** Color applied to the confirm button (default: "primary") */
  color?: 'error' | 'warning' | 'primary'
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.5);
`

const DialogPanel = styled.div`
  width: 90%;
  max-width: 480px;
  padding: ${({ theme }) => ut(asUnified(theme)).space('lg')};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 8}px;
  background: ${({ theme }) => asUnified(theme).bg?.canvas ?? '#ffffff'};
  box-shadow: ${({ theme }) => ut(asUnified(theme)).boxShadow('lg') ?? '0 10px 30px rgba(0,0,0,0.25)'};
`

const DialogTitleText = styled.h2`
  margin: 0 0 ${({ theme }) => ut(asUnified(theme)).space('sm')};
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.lg ?? '1.25rem'};
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
`

const DialogBodyText = styled.p`
  margin: 0 0 ${({ theme }) => ut(asUnified(theme)).space('lg')};
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
`

const DialogActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => ut(asUnified(theme)).space('sm')};
`

const CancelButton = styled.button`
  min-height: 36px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xxs')} ${ut(asUnified(theme)).space('md')}`};
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#cbd5e1'};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  background: none;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};
  font-weight: 600;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: 2px;
  }
`

const CONFIRM_COLORS: Record<'error' | 'warning' | 'primary', string> = {
  error: '#991b1b',
  warning: '#92400e',
  primary: '#1a56db',
}

const ConfirmActionButton = styled.button<{
  $color: 'error' | 'warning' | 'primary'
}>`
  min-height: 36px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xxs')} ${ut(asUnified(theme)).space('md')}`};
  border: none;
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  background: ${({ theme, $color }) => {
    const u = asUnified(theme)
    if ($color === 'error') return u.colors?.danger ?? CONFIRM_COLORS.error
    if ($color === 'warning') return u.colors?.warning ?? CONFIRM_COLORS.warning
    return u.colors?.primary ?? CONFIRM_COLORS.primary
  }};

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }
`

export const PamConfirmDialog = ({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  color = 'primary',
}: PamConfirmDialogProps): JSX.Element | null => {
  // Escape closes the dialog, mirroring MUI Dialog's default onClose(reason
  // ='escapeKeyDown') behaviour (which the previous implementation mapped
  // straight to onCancel).
  useEffect(() => {
    if (!open) return undefined
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, onCancel])

  if (!open) return null

  return createPortal(
    <Overlay onClick={onCancel}>
      <DialogPanel
        role="dialog"
        aria-modal="true"
        aria-labelledby="pam-confirm-dialog-title"
        aria-describedby="pam-confirm-dialog-description"
        onClick={(event) => event.stopPropagation()}
      >
        <DialogTitleText id="pam-confirm-dialog-title">
          {title}
        </DialogTitleText>
        <DialogBodyText id="pam-confirm-dialog-description">
          {body}
        </DialogBodyText>
        <DialogActionsRow>
          <CancelButton type="button" onClick={onCancel}>
            {cancelLabel}
          </CancelButton>
          <ConfirmActionButton
            type="button"
            $color={color}
            onClick={onConfirm}
            autoFocus
          >
            {confirmLabel}
          </ConfirmActionButton>
        </DialogActionsRow>
      </DialogPanel>
    </Overlay>,
    document.body
  )
}
